import { deepClone } from "@/utils"

/**
 * getTreeNode
 * @return node based on nodeId
 */
const getTreeNode = ({ nodeId = "", tree = [] }) => {
	let foundNode

	const recursion = tree => {
		if (!tree) return

		tree.forEach(node => {
			if (node.id === nodeId) foundNode = node
			if (node?.children?.length) recursion(node.children)
		})
	}

	recursion(tree)
	return foundNode
}

/**
 * removeTreeNode
 * @return copy of the tree without removed node
 */
const removeTreeNode = ({ nodeId = "", tree = [], idKey = "id" }) => {
	const recursion = (arr, parent = null) => {
		arr.forEach((node, i) => {
			if (node[idKey] === nodeId) {
				if (parent) parent.children.splice(i, 1)
			}
			if (node?.children?.length) recursion(node.children, node)
		})
	}

	// Create a copy of the tree so that we don't mutate data in original tree
	let copyOfTree = deepClone(tree)

	// If we are deleting root node
	if (copyOfTree.find(item => item[idKey] === nodeId)) return copyOfTree.filter(item => item[idKey] !== nodeId)

	// When not deleting a root node
	recursion(copyOfTree)
	return copyOfTree
}

/**
 * removeTreeNodes
 * @return copy of the tree without all removed nodes
 */
const removeTreeNodes = ({ nodeIds = [], tree = [], idKey = "id" }) => {
	const recursion = arr => {
		arr.forEach(node => {
			if (node.children) node.children = node.children.filter(item => !nodeIds.includes(item[idKey]))
			if (node?.children?.length) recursion(node.children, node)
		})
	}

	// Create a copy of the tree so that we don't mutate data in original tree
	let copyOfTree = deepClone(tree)

	// If we are deleting root node
	if (copyOfTree.find(item => nodeIds.includes(item[idKey]))) return copyOfTree.filter(item => !nodeIds.includes(item[idKey]))

	// When not deleting a root node
	recursion(copyOfTree)
	return copyOfTree
}

/**
 * putTreeNode
 * @return copy of the tree with putted node
 */
const putTreeNode = ({ payload, tree, idKey = "id" }) => {
	let parentNode
	let index

	const recursion = (arr, parent = null) => {
		arr.forEach((node, i) => {
			if (node[idKey] === payload[idKey]) {
				index = i
				parentNode = parent
			}

			if (node?.children?.length && !parentNode) recursion(node.children, node)
		})
	}

	// Create a copy of the tree so that we don't mutate data in original tree
	let copyOfTree = deepClone(tree)
	recursion(copyOfTree)

	if (!parentNode) copyOfTree.splice(index, 1, payload)
	else parentNode.children.splice(index, 1, payload)

	return copyOfTree
}

/**
 * reorderTreeNode
 * @return copy of the tree with reordered node
 */
const reorderTreeNode = ({ nodeId, newIndex, oldIndex, tree = [] }) => {
	let parentNode

	const recursion = (arr, parent = null) => {
		arr.forEach(node => {
			if (node.id === nodeId) parentNode = parent
			if (node?.children?.length && !parentNode) recursion(node.children, node)
		})
	}

	// Create a copy of the tree so that we don't mutate data in original tree
	let copyOfTree = deepClone(tree)
	recursion(copyOfTree)

	// Reorder based on if it's root or noot
	if (!parentNode) move(copyOfTree, oldIndex, newIndex)
	else move(parentNode?.children, oldIndex, newIndex)

	// Helper function to reorder in an array
	function move(arr, from, to) {
		arr.splice(to, 0, arr.splice(from, 1)[0])
	}

	return copyOfTree
}

/**
 * updateTree
 * @return copy of the tree where updateFunction was applied to every node
 */
const updateTree = ({ updateFunction, tree = [] }) => {
	const recursion = (arr, parent = null) => {
		arr.forEach(node => {
			updateFunction(node, parent)
			if (node?.children?.length) recursion(node.children, node)
		})
	}

	// Create a copy of the tree so that we don't mutate data in original tree
	let copyOfTree = deepClone(tree)
	recursion(copyOfTree)

	return copyOfTree
}

export { getTreeNode, removeTreeNode, removeTreeNodes, putTreeNode, updateTree, reorderTreeNode }
